import React, { useCallback, useEffect, useRef, useState } from 'react';
import { reviews, ratingSummary, Review } from '../data/reviews';
import { company } from '../data/content';
import { StarIcon, ArrowRightIcon, ChevronLeftIcon, ChevronRightIcon, CheckIcon, QuoteIcon, GoogleIcon, FacebookIcon } from './icons';
function Stars({
  rating,
  className = 'text-sky-500'
}: {
  rating: number;
  className?: string;
}) {
  return <span className={`flex items-center gap-0.5 ${className}`} role="img" aria-label={`${rating} out of 5 stars`} data-id="element-536" data-mp="2w8toq96fhq8x">
      {Array.from({
      length: rating
    }).map((_, index) => <StarIcon key={index} className="h-4 w-4 fill-current" data-id="element-537" data-mp="351ojg8y9s3kg" data-mpi={index} />)}
    </span>;
}
function initials(name: string) {
  return name.split(' ').slice(0, 2).map(part => part[0]).join('');
}
function ReviewCard({
  review
}: {
  review: Review;
}) {
  return <figure className="flex h-full w-[86vw] shrink-0 snap-start flex-col rounded-lg border border-navy-100 bg-white p-6 sm:w-[380px]" data-id="element-538" data-mp="3jzgy6yvzsnxl">
      <div className="flex items-start justify-between gap-4" data-id="element-539" data-mp="2rcxfn373kvt1">
        <Stars rating={review.rating} data-id="element-540" data-mp="2mdklryht6ogy" />
        {review.source === 'Google' ? <a href={company.googleProfileUrl} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-navy-400 transition-colors duration-150 ease-out hover:text-navy-700" data-id="element-541" data-mp="2mfnlja8rjvyv">
            <GoogleIcon className="h-3.5 w-3.5" data-id="element-542" data-mp="3a4zty7w9o004" />
            Google
          </a> : review.source === 'Facebook' ? <a href={company.facebookUrl} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-navy-400 transition-colors duration-150 ease-out hover:text-navy-700" data-id="element-543" data-mp="30cty2n2w8yfn">
            <FacebookIcon className="h-3.5 w-3.5" data-id="element-544" data-mp="3o2obb5r4kp0f" />
            Facebook
          </a> : <span className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-navy-300" data-id="element-545" data-mp="1m54bx0qafq40">
            {review.source}
          </span>}
      </div>

      <QuoteIcon className="mt-5 h-5 w-5 text-navy-100" data-id="element-546" data-mp="1cfunpvykerl6" />
      <blockquote className="mt-2 text-[15px] leading-relaxed text-navy-500" data-id="element-547" data-mp="1cfb50d13bz63">
        {review.quote}
      </blockquote>

      {review.reply && <p className="mt-4 rounded-md bg-sand p-3 text-[13px] leading-relaxed text-navy-400" data-id="element-548" data-mp="13ed7mdl5qpwh">
          <span className="font-semibold text-navy-600" data-id="element-549" data-mp="18eis99t0fc52">Response from the owner:</span>{' '}
          {review.reply}
        </p>}

      <figcaption className="mt-auto flex items-center gap-3 border-t border-navy-100 pt-5" data-id="element-550" data-mp="ti9uydsoj8va">
        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-navy-800 font-display text-sm font-bold text-white" aria-hidden="true" data-id="element-551" data-mp="ygl6qu2jbtg5">
          {initials(review.name)}
        </span>
        <div className="min-w-0" data-id="element-552" data-mp="2srgmcgc508sf">
          <p className="flex items-center gap-1.5 font-display text-[15px] font-bold leading-tight text-navy-800" data-id="element-553" data-mp="h5ppnegwgdt3">
            {review.name}
            {review.verified && <span className="flex items-center gap-1 text-[11px] font-semibold text-sky-600" data-id="element-554" data-mp="qw0t6zwo8kzg">
                <CheckIcon className="h-3 w-3" data-id="element-555" data-mp="m5u6x6pmtckd" />
                Verified
              </span>}
          </p>
          <p className="truncate text-[13px] text-navy-400" data-id="element-556" data-mp="zw7ev92iwr9p">
            {review.location} · {review.job}
          </p>
          <p className="text-[12px] text-navy-300" data-id="element-557" data-mp="vulul2nntaph">{review.date}</p>
        </div>
      </figcaption>
    </figure>;
}
export function Reviews() {
  const trackRef = useRef<HTMLUListElement>(null);
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(false);
  const updateBounds = useCallback(() => {
    const el = trackRef.current;
    if (!el) return;
    setAtStart(el.scrollLeft <= 4);
    setAtEnd(el.scrollLeft + el.clientWidth >= el.scrollWidth - 4);
  }, []);
  useEffect(() => {
    updateBounds();
    window.addEventListener('resize', updateBounds);
    return () => window.removeEventListener('resize', updateBounds);
  }, [updateBounds]);
  const scrollByCard = (direction: 1 | -1) => {
    const el = trackRef.current;
    if (!el) return;
    const card = el.querySelector('li');
    const amount = card ? card.clientWidth + 24 : el.clientWidth * 0.8;
    el.scrollBy({
      left: amount * direction,
      behavior: 'smooth'
    });
  };
  return <section id="reviews" className="w-full bg-sand py-20 lg:py-28" data-id="element-558" data-mp="2ucnydeu6ffe5">
      <div className="mx-auto max-w-content px-5 lg:px-8" data-id="element-559" data-mp="2uaun1m8mc6zt">
        <div className="flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between" data-id="element-560" data-mp="2zbifmcchwvw5">
          <div className="max-w-xl" data-id="element-561" data-mp="3441zts5oup9x">
            <p className="text-sm font-semibold text-sky-600" data-id="element-562" data-mp="3453f68tqlxxg">Real reviews, real backyards</p>
            <h2 className="mt-3 font-display text-3xl font-extrabold leading-tight tracking-tight text-navy-800 sm:text-4xl" data-id="element-563" data-mp="26liiizo8kn3u">
              What our neighbors say after the water goes back in.
            </h2>
            <div className="mt-6 flex flex-col gap-4 rounded-lg border border-navy-100 bg-white p-5 sm:flex-row sm:items-center sm:gap-6" data-id="element-564" data-mp="sp8tczh8vs99">
              <div className="flex items-center gap-4" data-id="element-565" data-mp="jq3svnm4houg">
                <GoogleIcon className="h-8 w-8 shrink-0" data-id="element-566" data-mp="jsq0fn90k4co" />
                <div data-id="element-567" data-mp="er0n20ybjelf">
                  <p className="flex items-center gap-2" data-id="element-568" data-mp="erk99tv04p59">
                    <span className="font-display text-3xl font-extrabold leading-none tracking-tight text-navy-800" data-id="element-569" data-mp="9sz37dt2jb8w">
                      {ratingSummary.average}
                    </span>
                    <Stars rating={5} data-id="element-570" data-mp="zvsvedpzeih" />
                  </p>
                  <p className="mt-1 text-[13px] text-navy-400" data-id="element-571" data-mp="y19i4i3jugi">
                    {ratingSummary.count} reviews · {ratingSummary.rehire} rehire us
                  </p>
                </div>
              </div>
              <div className="flex flex-col gap-2 sm:ml-auto" data-id="element-572" data-mp="3i8iui7a6ijlb">
                <a href={company.googleProfileUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 rounded-md bg-navy-800 px-4 py-2.5 text-sm font-bold text-white transition-colors duration-150 ease-out hover:bg-navy-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-400" data-id="element-573" data-mp="3i92avjk6kzx8">
                  Read all on Google
                  <ArrowRightIcon className="h-4 w-4" data-id="element-574" data-mp="3rc6bi4p8nbh4" />
                </a>
                <a href={company.googleReviewUrl} target="_blank" rel="noopener noreferrer" className="text-center text-[13px] font-semibold text-sky-600 underline-offset-2 transition-colors duration-150 ease-out hover:underline" data-id="element-575" data-mp="3s8cq5swr4p">
                  Leave us a review
                </a>
              </div>
            </div>
          </div>

          <div className="flex gap-3" data-id="element-576" data-mp="ixpdtqwu4job">
            <button type="button" onClick={() => scrollByCard(-1)} disabled={atStart} aria-label="Previous reviews" className="flex h-12 w-12 items-center justify-center rounded-full border border-navy-200 bg-white text-navy-700 transition-colors duration-150 ease-out hover:border-navy-400 disabled:opacity-35 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-400" data-id="element-577" data-mp="23li0x31f75uk">
              <ChevronLeftIcon className="h-5 w-5" data-id="element-578" data-mp="1fokthnethmd6" />
            </button>
            <button type="button" onClick={() => scrollByCard(1)} disabled={atEnd} aria-label="Next reviews" className="flex h-12 w-12 items-center justify-center rounded-full border border-navy-200 bg-white text-navy-700 transition-colors duration-150 ease-out hover:border-navy-400 disabled:opacity-35 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-400" data-id="element-579" data-mp="1ulbrptcr8s17">
              <ChevronRightIcon className="h-5 w-5" data-id="element-580" data-mp="13dtf8v1632xv" />
            </button>
          </div>
        </div>
      </div>

      <div className="relative mt-12" data-id="element-581" data-mp="18d6bg6dxhmd4">
        <ul ref={trackRef} onScroll={updateBounds} tabIndex={0} aria-label="Customer reviews carousel" className="flex snap-x snap-mandatory gap-6 overflow-x-auto scroll-smooth px-5 pb-4 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden lg:px-[max(2rem,calc((100vw-1180px)/2+2rem))]" data-id="element-582" data-mp="18c4rfcexppj9">
          {reviews.map(review => <li key={review.name} className="flex shrink-0 snap-start" data-id="element-583" data-mp="1r4fp8ruqexai">
              <ReviewCard review={review} data-id="element-584" data-mp="1w28vfkw1bras" />
            </li>)}
        </ul>
      </div>

      <div className="mx-auto mt-6 max-w-content px-5 lg:px-8" data-id="element-585" data-mp="3sv0vfx7t2rc2">
        <p className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-navy-400" data-id="element-586" data-mp="3swvh5dqwinh7">
          Reviews pulled from our Google Business Profile, Facebook and Nextdoor.
          <a href={company.googleProfileUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 font-semibold text-sky-600 transition-colors duration-150 ease-out hover:text-sky-500" data-id="element-587" data-mp="3qb4hhd48l817">
            <GoogleIcon className="h-3.5 w-3.5" data-id="element-588" data-mp="3bm820uzuosqk" />
            See the profile
          </a>
          <span aria-hidden="true" data-id="element-589" data-mp="d4cibs1g2mdq">·</span>
          <a href={company.phoneHref} className="inline-flex items-center gap-1.5 font-semibold text-sky-600 transition-colors duration-150 ease-out hover:text-sky-500" data-id="element-590" data-mp="d31ghxrk7op9">
            Call {company.phoneDisplay}
            <ArrowRightIcon className="h-4 w-4" data-id="element-591" data-mp="3uc6h3v22fm3p" />
          </a>
        </p>
      </div>
    </section>;
}